import React, { useEffect, useMemo, useState } from "react";
import { Box, Paper, Grid, TextField, MenuItem, Button, Snackbar, Alert } from "@mui/material";
import { useMasterData } from "../../../hooks/useMasterData";
import HouseAssignTable from "./HouseAssignTable";
import HouseAssignForm from "./HouseAssignForm";
import { studentHouseApi, StudentRow, HouseItem } from "./studentHouseApi";

const HouseAssignPage: React.FC = () => {
  const { masters, fetchMasters, fetchSections, fetchAcademicSessions } = useMasterData();
  const [filters, setFilters] = useState({ academicSessionId: "", classId: "", sectionId: "" });
  const [sections, setSections] = useState<{ id: string; name: string }[]>([]);
  const [rows, setRows] = useState<StudentRow[]>([]);
  const [houses, setHouses] = useState<HouseItem[]>([]);
  const [openForm, setOpenForm] = useState(false);
  const [current, setCurrent] = useState<StudentRow | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "info" }>({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    fetchMasters();
    fetchAcademicSessions();
  }, []);

  useEffect(() => {
    if (filters.classId) {
      fetchSections(filters.classId).then((s) => setSections(s));
    } else {
      setSections([]);
      setFilters((p) => ({ ...p, sectionId: "" }));
    }
  }, [filters.classId]);

  const canLoad = useMemo(() => !!filters.academicSessionId && !!filters.classId && !!filters.sectionId, [filters]);

  const loadStudents = async () => {
    if (!canLoad) {
      setSnackbar({ open: true, message: "Select Academic Session, Class and Section", severity: "error" });
      return;
    }
    try {
      const [students, hs] = await Promise.all([studentHouseApi.getStudents(filters), studentHouseApi.getHouses()]);
      setRows(students);
      setHouses(hs);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to load data", severity: "error" });
    }
  };

  const openAssign = (row: StudentRow) => {
    setCurrent(row);
    setOpenForm(true);
  };

  const handleSubmit = async (data: { houseId: string; assignedDate: string }) => {
    if (!current) return;
    try {
      await studentHouseApi.assign({
        studentId: current.studentId,
        houseId: data.houseId,
        assignedDate: data.assignedDate,
        academicSessionId: filters.academicSessionId,
        classId: filters.classId,
        sectionId: filters.sectionId,
      });
      setSnackbar({ open: true, message: "House assigned", severity: "success" });
      setOpenForm(false);
      loadStudents();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || "Failed to assign house", severity: "error" });
    }
  };

  const className = masters.classes.find((c) => c.id === filters.classId)?.name || "";
  const sectionName = sections.find((s) => s.id === filters.sectionId)?.name || "";

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ fontSize: 22, fontWeight: 600, mb: 2 }}>House Assign</Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Academic Session"
              value={filters.academicSessionId}
              onChange={(e) => setFilters({ ...filters, academicSessionId: e.target.value })}
            >
              {masters.academicSessions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth label="Class" value={filters.classId} onChange={(e) => setFilters({ ...filters, classId: e.target.value })}>
              {masters.classes.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Section"
              value={filters.sectionId}
              disabled={!filters.classId}
              onChange={(e) => setFilters({ ...filters, sectionId: e.target.value })}
            >
              {sections.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button fullWidth variant="contained" onClick={loadStudents}>
              Load Students
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <HouseAssignTable rows={rows} onAssign={openAssign} />

      {current && (
        <HouseAssignForm
          open={openForm}
          onClose={() => setOpenForm(false)}
          houses={houses}
          studentName={current.studentName}
          className={className}
          sectionName={sectionName}
          onSubmit={handleSubmit}
        />
      )}

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HouseAssignPage;
